import React, { useEffect, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { apiErrorCode } from "@/lib/apiErrors";
import { catalogueErrorMessage } from "@/lib/catalogueErrors";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Tag } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { requiredField } from "../lib/validation";
import { isChoosablePromoCode, refusesCodeCharset } from "../lib/promoCode";

// The kinds a promo code may cover, in the order the backend whitelists them
// (modules/promos.DISCOUNTABLE_KINDS). Gift cards and invoices are ABSENT and cannot be
// added: discounting stored value is arbitrage, and an invoice is a priced agreement
// already sent to a client. The server refuses either outright — this list is the UI
// telling the practitioner the same thing rather than offering a checkbox that 422s.
//
// "telehealth" IS NOT HERE, for the reason the server gives (modules/promos.py): a
// consultation is sold per MINUTE against an authorised ceiling (Flow C), so there is no
// quoted total for a discount to reduce, and the whitelist dropped it. It was listed here
// for a while after the server stopped accepting it — and because the default selection
// below is EVERY kind, that made the untouched form post a refused member and no promo
// code could be created at all (BUG-221). __tests__/PromosAdmin.appliesTo.test.js pins
// this array against the Python tuple so the mirror cannot rot again in either direction.
//
// A code created while "telehealth" was whitelisted still names it, and keeps naming it:
// `applies_to` is the row's own statement and is never re-derived from this list — which
// is why `promos.kind.telehealth` keeps its label in the dictionary for the list below.
const KINDS = ["booking", "product", "program", "course", "pathway"];

// The form speaks the units the API speaks — basis points and cents — but never shows
// them: the practitioner types "20" (%) or "12.50" ($), and the conversion happens at
// exactly one place, `toPayload`. A form that stored 2000 and displayed 2000 is how the
// legacy `value: float` field became ambiguous in the first place.
const EMPTY = {
  code: "",
  discount_type: "percent",
  percent: "20",
  amount: "10.00",
  applies_to: [...KINDS],
  min_subtotal: "0",
  max_uses: 0,
  active: true,
};

// modules/promos.PERCENT_BP_SCALE — 10000 bp = 100 %.
const PERCENT_BP_SCALE = 10000;

// Hundredths of what the practitioner typed, rounded: basis points from a percentage,
// cents from an amount. ONE conversion, called by BOTH the schema and `toPayload`, so the
// number the form judges is exactly the number the server receives — a schema that judged
// "0.004 %" as a positive percentage while the payload rounded it to 0 bp would refuse
// nothing and 422 anyway.
const toHundredths = (v) => Math.round(parseFloat(v || 0) * 100);

// Mirrors modules/promos.py's PromoCode/PromoCodeIn, rule for rule. Every floor below is
// the server's own, and mirroring them is the whole of BUG-220: a rule the form does not
// judge comes back as FastAPI's 422, whose `msg` is English developer prose that
// `lib/apiErrors.extractApiError` may not show a FR/ES practitioner (language law), so
// what they actually read is "max_uses : Invalid value" — or, for the model-level pairing
// errors, which carry `loc: ["body"]` and no field at all, a bare "Invalid value".
//   · `code`               required, max 64 (min_length=1, max_length=64), and only the
//                          charset `lib/promoCode` mirrors from `has_visible_charset`
//   · a PERCENT code       1..10000 basis points — 0 % is refused by `_pairing`, so the
//                          floor is 0.01 %, not 0
//   · an AMOUNT code       amount_cents > 0
//   · both counters        `Field(0, ge=0)`, and `max_uses` is an integer
//   · `applies_to`         non-empty (`_whitelist` refuses an empty list outright) and,
//                          by construction, only KINDS members
const PROMO_SCHEMA = z.object({
  // The charset rule stays quiet on an empty code: "required" already says it.
  code: requiredField(64).refine((v) => v === "" || isChoosablePromoCode(v), "promos.codeCharset"),
  discount_type: z.enum(["percent", "amount"]),
  percent: z.string(),
  amount: z.string(),
  applies_to: z.array(z.string()).min(1, "promos.appliesToRequired"),
  // Coerced: the inputs hold strings, and "" (a cleared field) has to read as 0 — the
  // same value `toPayload` sends. `invalid_type_error` is what keeps a NaN's message an
  // i18n KEY instead of zod's English "Expected number, received nan" (the convention in
  // lib/validation.js's header, and the trap `optionalEmailField`'s errorMap exists for).
  max_uses: z.coerce.number({ invalid_type_error: "promos.maxUsesInvalid" })
    .int("promos.maxUsesInvalid")
    .min(0, "promos.maxUsesInvalid"),
  min_subtotal: z.coerce.number({ invalid_type_error: "promos.minSubtotalInvalid" })
    .min(0, "promos.minSubtotalInvalid"),
}).superRefine((val, ctx) => {
  if (val.discount_type === "percent") {
    const bp = toHundredths(val.percent);
    if (!Number.isFinite(bp) || bp < 1 || bp > PERCENT_BP_SCALE) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["percent"], message: "promos.percentInvalid" });
    }
  } else {
    const cents = toHundredths(val.amount);
    if (!Number.isFinite(cents) || cents < 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["amount"], message: "promos.amountInvalid" });
    }
  }
});

// The refusal only THIS endpoint raises, mapped HERE rather than in
// lib/catalogueErrors.js's shared table: that table is for codes several catalogue
// editors receive, and lib/apiErrors.js's own rule is that a code one caller maps stays
// out of the shared one, so the wording of a code cannot drift between two screens.
// It is sent as `{code, message}` (modules/promos.PROMO_CODE_EXISTS) precisely so the
// sentence can be written in the practitioner's language instead of relaying the server's
// English diagnostic. Uniqueness is also the only promo rule a per-document model cannot
// state, which is why it is the only one that reaches the client as a code rather than as
// a field the form could have judged before posting.
const OWN_ERROR_KEYS = {
  PROMO_CODE_EXISTS: "promos.errorDuplicate",
};

const toPayload = (f) => ({
  code: f.code.trim().toUpperCase(),
  discount_type: f.discount_type,
  percent_bp: f.discount_type === "percent" ? toHundredths(f.percent) : 0,
  amount_cents: f.discount_type === "amount" ? toHundredths(f.amount) : 0,
  // NO `currency`. The form never showed a currency input — it sent a hardcoded "cad",
  // which denominated every amount code in a currency the platform may not settle in, and
  // such a code is refused at checkout as a mismatch. The server denominates an amount
  // code in the platform's settlement currency, which is the one value the ledger accepts.
  applies_to: f.applies_to,
  min_subtotal_cents: toHundredths(f.min_subtotal),
  max_uses: parseInt(f.max_uses, 10) || 0,
  active: f.active,
});

export default function PromosAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const load = async () => {
    const { data } = await api.get("/admin/promo-codes");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };

  const toggleKind = (k) => {
    setForm((f) => ({
      ...f,
      applies_to: f.applies_to.includes(k)
        ? f.applies_to.filter((x) => x !== k)
        : [...f.applies_to, k],
    }));
    clearFieldError("applies_to");
  };

  const create = async () => {
    if (validate(PROMO_SCHEMA, form)) { toast.error(tc("validation.fixHighlighted")); return; }
    setBusy(true); setError("");
    try {
      await api.post("/admin/promo-codes", toPayload(form));
      setForm(EMPTY);
      await load();
    } catch (e) {
      // Own code first, then the shared catalogue mapper (this IS a catalogue write, and
      // `catalogueErrorMessage` is what keeps `resolved_currency`'s refusal from reaching
      // the practitioner as English prose); `extractApiError` is its last resort.
      const own = OWN_ERROR_KEYS[apiErrorCode(e)];
      // A 422 on the code's value (a charset rule the form missed) reads exactly like the
      // inline refusal, not FastAPI's English "Value error, ..." prose.
      if (refusesCodeCharset(e)) setError(t("promos.codeCharset"));
      else setError(own ? t(own) : catalogueErrorMessage(e, tc, tc("errors.saveFailed")));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("promos.confirmDelete"))) return;
    try {
      await api.delete(`/admin/promo-codes/${id}`);
      load();
    } catch (e) {
      setError(extractApiError(e, tc("errors.saveFailed"), tc));
    }
  };

  const describe = (p) =>
    p.discount_type === "amount"
      ? t("promos.discountFixed", { value: (p.amount_cents / 100).toFixed(2) })
      : t("promos.discountPct", { value: p.percent_bp / 100 });

  return (
    <div data-testid="admin-promos">
      <h1 className="font-display text-4xl">{t("promos.title")}</h1>
      <p className="text-eh-muted mt-1">{t("promos.subtitle")}</p>

      <div className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <Inp
            label={t("promos.code")}
            value={form.code}
            testid="promo-code"
            error={fieldErrors.code ? (fieldErrors.code.startsWith("promos.") ? t(fieldErrors.code) : tc(fieldErrors.code)) : ""}
            onChange={(v) => updateField("code", v.toUpperCase())}
          />
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("promos.type")}</label>
            <select
              value={form.discount_type}
              onChange={(e) => updateField("discount_type", e.target.value)}
              className="mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm"
              data-testid="promo-type"
            >
              <option value="percent">{t("promos.percentage")}</option>
              <option value="amount">{t("promos.fixed")}</option>
            </select>
          </div>
          {form.discount_type === "percent" ? (
            <Inp
              label={t("promos.percentValue")}
              type="number"
              value={form.percent}
              testid="promo-percent"
              error={fieldErrors.percent ? t(fieldErrors.percent, null, "Enter a percentage above 0 and up to 100.") : ""}
              onChange={(v) => updateField("percent", v)}
            />
          ) : (
            <Inp
              label={t("promos.amountValue")}
              type="number"
              value={form.amount}
              testid="promo-amount"
              error={fieldErrors.amount ? t(fieldErrors.amount, null, "Enter an amount greater than 0.") : ""}
              onChange={(v) => updateField("amount", v)}
            />
          )}
          <Inp
            label={t("promos.maxUses")}
            type="number"
            value={form.max_uses}
            testid="promo-max-uses"
            error={fieldErrors.max_uses ? t(fieldErrors.max_uses, null, "Enter a whole number of uses, 0 or more (0 = unlimited).") : ""}
            onChange={(v) => updateField("max_uses", v)}
          />
          <Inp
            label={t("promos.minSubtotal")}
            type="number"
            value={form.min_subtotal}
            testid="promo-min-subtotal"
            error={fieldErrors.min_subtotal ? t(fieldErrors.min_subtotal, null, "Enter a minimum order of 0 or more.") : ""}
            onChange={(v) => updateField("min_subtotal", v)}
          />
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("promos.appliesTo")}</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleKind(k)}
                  data-testid={`promo-kind-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${
                    form.applies_to.includes(k)
                      ? "bg-eh-ink text-eh-bg border-eh-ink"
                      : "bg-white text-eh-muted border-eh-border-soft"
                  }`}
                >
                  {t(`promos.kind.${k}`)}
                </button>
              ))}
            </div>
            <FieldError
              message={fieldErrors.applies_to ? t(fieldErrors.applies_to, null, "Choose at least one kind this code applies to.") : ""}
              testId="promo-appliesTo-error"
            />
          </div>
          <button
            onClick={create}
            disabled={busy}
            className="btn-primary text-sm h-[40px]"
            data-testid="promo-create"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} {t("promos.create")}
          </button>
        </div>
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
      </div>

      <div className="mt-8 space-y-2">
        {items.length === 0 && <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-8 text-center">{t("promos.empty")}</div>}
        {items.map((p) => (
          <div key={p.id} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-eh-bg flex items-center justify-center text-eh-sage-deep"><Tag size={16} /></div>
            <div className="flex-1">
              <div className="font-mono text-base text-eh-ink">{p.code}</div>
              <div className="text-xs text-eh-muted">
                {describe(p)} · {p.used_count}/{p.max_uses || "∞"} {t("promos.uses")} · {p.active ? t("promos.active") : t("promos.inactive")}
              </div>
              <div className="text-xs text-eh-muted mt-0.5">
                {(p.applies_to || []).map((k) => t(`promos.kind.${k}`)).join(" · ")}
                {p.min_subtotal_cents > 0 && ` · ${t("promos.minSubtotalLabel", { value: (p.min_subtotal_cents / 100).toFixed(2) })}`}
              </div>
            </div>
            <button onClick={() => remove(p.id)} className="p-2 rounded-full text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const Inp = ({ label, value, onChange, type = "text", testid, error }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm ${error ? invalidInputClass : ""}`}
    />
    <FieldError message={error} testId={testid ? `${testid}-error` : undefined} />
  </div>
);
